'use client';

import { useMergeRefs } from '@floating-ui/react';
import {
  type ComponentProps,
  type MouseEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { Slot } from '../slot';
import { ModalRootContext } from './context/modal-root.context';
import { useModalRegistry } from './hooks/use-modal-registry';
import type { ModalTriggerPropsInterface } from './types/modal-trigger.types';

/**
 * @since 10.0.0
 */
export const ModalTrigger = forwardRef<HTMLButtonElement, ModalTriggerPropsInterface>(
  (props: ModalTriggerPropsInterface, forwardedRef) => {
    const { children, multiModalTriggerId, onClick, ...rest } = props;
    const { open } = useModalRegistry();
    const { modal } = useContext(ModalRootContext);

    // Modal could be undefined if using multi modal triggers
    const mergedRefs = useMergeRefs([modal?.refs.setReference, forwardedRef]);

    // When multi modal trigger id is used, open modal via registry
    const onClickCallback = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        open(multiModalTriggerId, event);
        onClick?.(event);
      },
      [multiModalTriggerId, open, onClick],
    );

    const explicitTriggerProps: ComponentProps<'button'> = useMemo(() => {
      if (multiModalTriggerId) {
        return { ...rest, onClick: onClickCallback };
      }

      const mergedReferenceProps = modal.getReferenceProps(rest) as ComponentProps<'button'>;
      const {
        'aria-controls': _ariaControls,
        'aria-expanded': _ariaExpanded,
        'aria-haspopup': _ariaHasPopup,
        ...referenceProps
      } = mergedReferenceProps;

      return {
        ...referenceProps,
      };
    }, [multiModalTriggerId, onClickCallback, modal, rest]);

    return (
      <Slot
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Modal.Trigger"
        // Conditionally swap ref, props, and handlers between modal registry and floating UI
        ref={multiModalTriggerId ? forwardedRef : mergedRefs}
        {...explicitTriggerProps}
      >
        {children}
      </Slot>
    );
  },
);

ModalTrigger.displayName = 'Modal.Trigger';
